import { useNavigate } from 'react-router-dom'
import { Monitor } from 'lucide-react'
import ScreenShell from 'components/ScreenShell/ScreenShell'
import { Button } from 'components/ui/button'
import routes from 'constants/routes'
import { fieldRoutes } from 'constants/fieldRoutes'
import useAppMode from 'features/Field/useAppMode'

/**
 * Shown when a mobile visitor reaches a surface that only works on a desktop:
 * Explorer's data grid, the Evaluations workspaces, the Admin tools.
 *
 * An explicit notice was chosen over a silent redirect to Home: a bookmark that
 * quietly lands somewhere else reads as a bug, whereas this explains itself and
 * offers a way on.
 *
 * **Where "on" leads is the part that changed with the mode fork.** It used to
 * be the desktop home, which a phone could still render. It cannot any more, so
 * a phone is sent to the field app by name rather than to a `/` that would
 * bounce it there anyway. Saying "Back to Home" and landing somewhere called
 * something else is how a fix reads as a second bug.
 *
 * A tablet still has a desktop home worth returning to, and still has the
 * switch in the header, so it keeps the original wording.
 *
 * It renders inside ScreenShell deliberately. Rendered bare, the header would
 * disappear, and with the sidebar's User section hidden on mobile the header
 * avatar is the ONLY remaining route to User Profile and Logout. A bare screen
 * here would be a dead end with no way to sign out.
 */
const NotAvailableOnMobile = () => {
	const navigate = useNavigate()
	const { canChoose } = useAppMode()

	return (
		<ScreenShell>
			<div className="p-4">
				<div className="flex flex-col items-center justify-center text-center py-12 px-6 w-full bg-white border border-dashed border-black/10 rounded-xl">
					<div className="flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 mb-6">
						<Monitor className="w-12 h-12 text-primary" />
					</div>
					<h3 className="text-[1.375rem] font-bold text-gray-800 mb-2">Not available on mobile</h3>
					<p className="max-w-[520px] mx-auto mb-8 leading-relaxed text-gray-600">
						This section is built for a larger screen. Open MYDealTeams on a desktop to use it.
					</p>
					{canChoose ? (
						<Button variant="primary" isInput onClick={() => navigate(routes.HOME)}>
							Back to Home
						</Button>
					) : (
						<Button variant="primary" isInput onClick={() => navigate(fieldRoutes.HOME)}>
							Back to the field app
						</Button>
					)}
				</div>
			</div>
		</ScreenShell>
	)
}

export default NotAvailableOnMobile
